import React, { useContext } from "react";
import { graphql } from "gatsby";
import { LayoutContainer } from "../containers/layout";
import PageHeadMeta from "../components/global/page-head-meta";
import { cn, mapEdgesToNodes, toPlainText } from "../lib/helpers";
import { CardContext } from "../components/card/context";
import { GetStarted, SectionWrapper } from "../components/sections";
import { BackToZone } from "../components/back-to-zone";
import { SiteContext } from "../components/global/site-context";
import { CardGrid } from "../components/card/card-grid";
import { SignalIcon } from "../components/icon/signal";
import { TinyPills } from "../components/card/card";
import PortableText from "../components/portableText";

// query PromptTemplateQuery($id: String!, $signalSources: [String!]) {

export const query = graphql`
  query PromptTemplateQuery($id: String!) {
    doc: sanityPrompt(id: { eq: $id }) {
      _type
      id
      _id
      title
      slug {
        current
      }
      status
      _rawExcerpt(resolveReferences: { maxDepth: 5 })
      _rawBody(resolveReferences: { maxDepth: 12 })
      sourceType
      categories {
        _id
        title
        titlePublic
        slug {
          current
        }
        taxonomy
      }
      integrations {
        ...Integration
      }
      signalSource {
        _id
        title
        slug {
          current
        }
      }
      strength
      contentRefs {
        ... on SanityPlaybook {
          ...cardNodePlaybook
        }
      }
    }
    playbooks: allSanityPlaybook(
      limit: 4
      sort: { fields: [_createdAt], order: DESC }
      filter: { status: { eq: "published" }, _rawExcerpt: { ne: null } }
    ) {
      edges {
        node {
          ...cardNodePlaybook
        }
      }
    }
    # relatedPrompts: allSanityPrompt(
    #   limit: 6
    #   sort: { fields: [_createdAt], order: [DESC] }
    #   filter: {
    #     slug: { current: { ne: null } }
    #     status: { eq: "published" }
    #     id: { ne: $id }
    #     signalSource: { elemMatch: { _id: { in: $signalSources } } }
    #   }
    # ) {
    #   edges {
    #     node {
    #       ...cardNodePrompt
    #     }
    #   }
    # }
  }
`;

const PromptTemplate = ({ data }) => (
  <LayoutContainer doc={{ ...data?.doc }}>
    <PageHeadMeta
      title={data?.doc.title}
      description={toPlainText(data?.doc._rawExcerpt)}
      image={data?.doc.mainImage}
      doc={data?.doc}
    />
    {data?.doc && (
      <CardContext.Provider value={{ ...data?.doc }}>
        <PromptPage
          {...data?.doc}
          related={data?.doc.contentRefs.concat(
            mapEdgesToNodes(data?.playbooks)
          )}
        />
      </CardContext.Provider>
    )}
  </LayoutContainer>
);

export default PromptTemplate;

const CATEGORY_MAP = new Map([["Sales", "Sales-led"]]);
const PAGE_SIZE = "thin";

const PromptPage = ({
  title,
  _rawBody,
  sourceType,
  categories,
  related,
  strength,
}) => {
  const site = useContext(SiteContext);

  return (
    <>
      <SectionWrapper
        {...{
          container: "thin",
          noHeaders: true,
          name: "header",
          padding: "tight",
          background: "lavenderUltraLight",
        }}
      >
        <div className={"pt-12 md:pt-16"}>
          <BackToZone to="/prompts/" text="See all prompts" />
        </div>
        <h1 className="text-5xl font-semibold mb-8 mr-24">{title}</h1>

        {categories && sourceType && strength && (
          <div className="border border-green-emerald p-3 rounded-2xl">
            <ul className="grid md:grid-cols-3 gap-2">
              <li className="bg-white border border-dark-10 p-4 lg:p-6 rounded-lg">
                <p className="text-secondary text-sm font-medium mb-4">
                  Go-to-market motion
                </p>
                <div className="flex flex-wrap gap-1 text-sm font-medium">
                  {categories
                    .filter((c) => c.taxonomy.includes("motions"))
                    .map((n, i) => (
                      <div
                        className="bg-dark-2 border border-dark-10 py-1 px-2 rounded-md"
                        key={n._id}
                      >
                        <a
                          href={`/resources/${n.slug.current}`}
                          className="hover:text-link"
                        >
                          {CATEGORY_MAP.get(n.titlePublic) ||
                            n.titlePublic ||
                            n.title}
                        </a>
                      </div>
                    ))}
                </div>
              </li>
              <li className="bg-white border border-dark-10 p-4 lg:p-6 rounded-lg">
                <p className="text-secondary text-sm font-medium mb-4">
                  Signal source
                </p>
                <TinyPills list={sourceType} />
              </li>
              <li className="bg-white border border-dark-10 p-4 lg:p-6 rounded-lg">
                <p className="text-secondary text-sm font-medium mb-4">
                  Signal strength
                </p>
                <SignalPill strength={strength} />
              </li>
            </ul>
          </div>
        )}
      </SectionWrapper>

      <SectionWrapper padding="bottom" container={PAGE_SIZE}>
        {/* <TableOfContentsSection {...{ _rawBody }} /> */}
        {_rawBody && <PortableText blocks={_rawBody} className="pt-8" />}
      </SectionWrapper>

      {/* use signal category tags to filter related playbooks */}
      {related && (
        <SectionWrapper
          container="large"
          header="Related playbooks"
          background="lavenderUltraLight"
        >
          <CardGrid
            nodes={related.slice(0, 6)}
            maxColumns={3}
            browseMoreHref="/playbooks/"
            browseMoreText="See all playbooks"
            hideEvents={true}
            hideType={true}
            showSummary={true}
            attribution={{ action: "suggestion" }}
            panels={true}
            makeThumbnail={true}
          />
        </SectionWrapper>
      )}

      <SectionWrapper background="mint" container="large" name="getStarted">
        <GetStarted />
      </SectionWrapper>
    </>
  );
};

const STRENGTH_MAP = new Map([
  ["High", { bgColor: "mint", textColor: "mintDark", file: "high.svg" }],
  ["Medium", { bgColor: "peach", textColor: "peachDark", file: "medium.svg" }],
  ["Low", { bgColor: "melonLight", textColor: "red", file: "low.svg" }],
]);

export const SignalPill = ({ strength }) =>
  strength ? (
    <div
      className={cn(
        "inline-block px-2 py-1 uppercase rounded-md text-xs",
        `bg-${STRENGTH_MAP.get(strength).bgColor}`,
        `text-${STRENGTH_MAP.get(strength).textColor}`
      )}
    >
      <span className="flex gap-1 items-center">
        <SignalIcon strength={strength} />
        {strength} Intent
      </span>
    </div>
  ) : null;
